/**
 * Module dependencies.
 */

import { Text } from 'src/components/core/text';
import { media } from 'src/styles/media';
import { useRouter } from 'next/router';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import Parallax from 'src/components/core/animations/parallax';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Section` styled component.
 */

const Section = styled.section`
  padding-bottom: 3.5rem;

  ${media.min.md`
    padding-bottom: 5rem;
  `}
`;

/**
 * `TitleContainer` styled component.
 */

const TitleContainer = styled(Container)`
  position: relative;
  z-index: 1;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text)`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  position: relative;
  text-align: center;
  text-transform: uppercase;
  z-index: 1;
`;

/**
 * `Subtitle` styled component.
 */

const Subtitle = styled(Text)`
  color: var(--color-yellow);
  margin-bottom: 0;
  position: relative;
  text-transform: none;
  top: -0.5rem;

  ${media.min.md`
    top: -2.5rem;
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  ${media.max.md`
    padding: 0;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min.md`
    display: grid;
    grid-template-areas: '. book .';
    grid-template-columns: 3fr 6fr 3fr;
    margin-top: -4rem;
  `}
`;

/**
 * `ShapeWrapper` styled component.
 */

const ShapeWrapper = styled.div`
  height: 64%;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -55%);
  width: 84%;

  ${media.min.md`
    height: 68%;
    transform: translate(-50%, -50%);
    width: 66%;
  `}
`;

/**
 * `Shape` styled component.
 */

const Shape = styled.div`
  background: var(--color-dark);
  border-radius: 50%;
  height: 100%;
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: book;
  overflow: hidden;
  padding-bottom: 132%;
  position: relative;

  ${media.min.md`
    padding-bottom: 96.95%;
  `}
`;

/**
 * Export `TripHeaderSection` component.
 */

const TripHeaderSection = () => {
  const { locale, translate } = useTranslate();
  const { isReady } = useRouter();

  return (
    <Section>
      <TitleContainer>
        {isReady && (
          <FadeInUpAnimation options={{ transitionTime: 1 }}>
            <Title
              as={'h1'}
              variant={'titleM'}
            >
              {translate('trip-to-portugal:headerSection.title')}

              <Subtitle
                as={'span'}
                variant={'handwrittenM'}
              >
                {translate('trip-to-portugal:headerSection.subtitle')}
              </Subtitle>
            </Title>
          </FadeInUpAnimation>
        )}
      </TitleContainer>

      <StyledContainer>
        <Grid>
          <ImageWrapper>
            <ShapeWrapper>
              <Parallax
                fullType={'fullHeight'}
                speed={3}
                speedMobile={2}
              >
                <Shape />
              </Parallax>
            </ShapeWrapper>

            <Parallax
              fullType={'fullAbsolute'}
              speed={1}
            >
              <Image
                alt={translate('trip-to-portugal:headerSection.title')}
                layout={'fill'}
                objectFit={'contain'}
                src={`/images/book-${locale}.png`}
              />
            </Parallax>
          </ImageWrapper>
        </Grid>
      </StyledContainer>
    </Section>
  );
};

/**
 * Export `TripHeaderSection` component.
 */

export default TripHeaderSection;
